<template>
    <div class="report-card-generator">
      <div class="header">
        <h2>Student Report Card Generator</h2>
        <p>Generate, preview, and distribute student report cards</p>
      </div>
  
      <div class="filters-container card">
        <h3>Filter Students</h3>
        <div class="filter-grid">
          <div class="filter-group">
            <label for="template">Report Card Template</label>
            <select id="template" v-model="selectedTemplate" class="modern-select">
              <option value="default">Default</option>
              <option value="modern">Modern</option>
              <option value="minimal">Minimal</option>
              <option value="classic">Classic</option>
            </select>
          </div>
  
          <div class="filter-group">
            <label for="study-year">Study Year</label>
            <select id="study-year" v-model="selectedStudyYear" @change="fetchTerms" class="modern-select">
              <option value="">Select Study Year</option>
              <option v-for="year in studyYears" :key="year.id" :value="year.id">
                {{ year.year_study }}
              </option>
            </select>
          </div>
  
          <div class="filter-group">
            <label for="term">Term</label>
            <select id="term" v-model="selectedTerm" :disabled="!selectedStudyYear" @change="fetchClasses" class="modern-select">
              <option value="">Select Term</option>
              <option v-for="term in terms" :key="term.id" :value="term.id">
                {{ term.name }}
              </option>
            </select>
          </div>
  
          <div class="filter-group">
            <label for="class">Class</label>
            <select id="class" v-model="selectedClass" :disabled="!selectedTerm" @change="fetchSections" class="modern-select">
              <option value="">Select Class</option>
              <option v-for="classroom in classes" :key="classroom.id" :value="classroom.id">
                {{ classroom.name }}
              </option>
            </select>
          </div>
  
          <div class="filter-group">
            <label for="section">Section</label>
            <select id="section" v-model="selectedSection" :disabled="!selectedClass" class="modern-select">
              <option value="">All Sections</option>
              <option v-for="section in sections" :key="section.id" :value="section.id">
                {{ section.name }}
              </option>
            </select>
          </div>
        </div>
  
        <button 
          class="search-btn" 
          :disabled="!selectedTerm" 
          @click="fetchStudents"
        >
          <i class="fas fa-search"></i> Search Students
        </button>
      </div>
  
      <div class="students-container card" v-if="students.length > 0">
        <div class="section-header">
          <h3>Student List</h3>
          <div class="bulk-actions">
            <button @click="selectAllStudents" class="action-btn secondary">
              <i class="fas" :class="allSelected ? 'fa-times' : 'fa-check-square'"></i>
              {{ allSelected ? 'Deselect All' : 'Select All' }}
            </button>
            <button 
              @click="previewSelectedReportCards" 
              class="action-btn"
              :disabled="selectedStudents.length === 0"
            >
              <i class="fas fa-eye"></i> Preview Selected
            </button>
            <button 
              @click="downloadSelectedReportCards" 
              class="action-btn"
              :disabled="selectedStudents.length === 0"
            >
              <i class="fas fa-download"></i> Download Selected
            </button>
            <button 
              @click="emailSelectedReportCards" 
              class="action-btn primary"
              :disabled="selectedStudents.length === 0"
            >
              <i class="fas fa-envelope"></i> Email Selected
            </button>
          </div>
        </div>
  
        <div class="table-container">
          <table class="modern-table">
            <thead>
              <tr>
                <th class="checkbox-col"><input type="checkbox" v-model="allSelected" @change="toggleAllSelection"></th>
                <th>ID</th>
                <th>Name</th>
                <th>Class</th>
                <th>Section</th>
                <th class="actions-col">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="student in students" :key="student.id">
                <td class="checkbox-col">
                  <input 
                    type="checkbox" 
                    v-model="selectedStudents" 
                    :value="student.id"
                    class="student-checkbox"
                  >
                </td>
                <td>{{ student.id }}</td>
                <td class="student-name">{{ student.name }}</td>
                <td>{{ student.class?.name }}</td>
                <td>{{ student.section?.name || 'N/A' }}</td>
                <td class="actions-col">
                  <div class="action-buttons">
                    <button @click="previewReportCard(student.id)" class="action-btn small">
                      <i class="fas fa-eye"></i>
                    </button>
                    <button @click="downloadReportCard(student.id)" class="action-btn small">
                      <i class="fas fa-download"></i>
                    </button>
                    <button @click="emailReportCard(student.id)" class="action-btn small primary">
                      <i class="fas fa-envelope"></i>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
  
      <div class="loading-overlay" v-if="loading">
        <div class="loading-content">
          <div class="spinner"></div>
          <span>Loading...</span>
        </div>
      </div>
  
      <div class="empty-state card" v-if="!loading && students.length === 0 && filtersApplied">
        <i class="fas fa-user-graduate"></i>
        <h4>No students found</h4>
        <p>No students match the selected criteria. Try adjusting your filters.</p>
      </div>
  
      <!-- PDF Preview Modal -->
      <div v-if="showPdfModal" class="pdf-modal">
        <div class="modal-overlay" @click="closePdfModal"></div>
        <div class="modal-container">
          <div class="modal-header">
            <h3>Report Card Preview - {{ currentStudentName }}</h3>
            <button @click="closePdfModal" class="close-btn">
              <i class="fas fa-times"></i>
            </button>
          </div>
          <div class="pdf-viewer">
            <iframe 
              v-if="pdfUrl" 
              :src="pdfUrl" 
              width="100%" 
              height="100%"
              frameborder="0"
            ></iframe>
            <div v-else class="loading-pdf">
              <div class="spinner"></div>
              <span>Generating PDF...</span>
            </div>
          </div>
          <div class="modal-footer">
            <button @click="printPdf" class="action-btn">
              <i class="fas fa-print"></i> Print
            </button>
            <button @click="downloadCurrentPdf" class="action-btn primary">
              <i class="fas fa-download"></i> Download
            </button>
            <button @click="closePdfModal" class="action-btn secondary">
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  </template>
  
  <script>
  // Keep the script tag exactly as provided in the original code
  import axios from 'axios';
  
  const api = axios.create({
    baseURL: process.env.VUE_APP_API_BASE_URL || 'https://cale.caledonian.ly/api',
  });
  
  api.interceptors.request.use((config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  }, (error) => Promise.reject(error));
  
  export default {
    data() {
      return {
        studyYears: [],
        terms: [],
        classes: [],
        sections: [],
        students: [],
        selectedTemplate: 'default',
        selectedStudyYear: '',
        selectedTerm: '',
        currentStudentName: '',
        selectedClass: '',
        selectedSection: '',
        selectedStudents: [],
        allSelected: false,
        loading: false,
        filtersApplied: false,
        showPdfModal: false,
        pdfUrl: null,
        currentStudentId: null,
        currentTermId: null,
      };
    },
    computed: {
      hasValidFilters() {
        return this.selectedTerm;
      }
    },
    mounted() {
      this.fetchStudyYears();
    },
    methods: {
        async previewReportCard(studentId) {
      try {
        this.loading = true;
        const student = this.students.find(s => s.id === studentId);
        this.currentStudentId = studentId;
        this.currentStudentName = student?.name || '';
        this.currentTermId = this.selectedTerm;
        
        const response = await api.get(`/report-cards/${studentId}/${this.selectedTerm}/${this.selectedTemplate}`, {
          responseType: 'blob'
        });
        
        const blob = new Blob([response.data], { type: 'application/pdf' });
        this.pdfUrl = URL.createObjectURL(blob);
        this.showPdfModal = true;
      } catch (error) {
        console.error('Error previewing report card:', error);
        this.$toast.error('Failed to preview report card');
      } finally {
        this.loading = false;
      }
    },
  
    async downloadReportCard(studentId) {
      try {
        this.loading = true;
        const link = document.createElement('a');
        link.href = `${process.env.VUE_APP_API_BASE_URL || 'https://cale.caledonian.ly/api'}/report-cards/${studentId}/${this.selectedTerm}/${this.selectedTemplate}/download`;
        link.setAttribute('download', '');
        link.setAttribute('target', '_blank');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (error) {
        console.error('Error downloading report card:', error);
        this.$toast.error('Failed to download report card');
      } finally {
        this.loading = false;
      }
    },
      downloadCurrentPdf() {
        if (this.currentStudentId) {
          this.downloadReportCard(this.currentStudentId);
        }
      },
  
      printPdf() {
        const iframe = document.querySelector('.pdf-container iframe');
        if (iframe && iframe.contentWindow) {
          iframe.contentWindow.focus();
          iframe.contentWindow.print();
        }
      },
  
      closePdfModal() {
        if (this.pdfUrl) {
          URL.revokeObjectURL(this.pdfUrl);
        }
        this.showPdfModal = false;
        this.pdfUrl = null;
        this.currentStudentId = null;
        this.currentTermId = null;
      },
  
      async previewSelectedReportCards() {
        if (this.selectedStudents.length === 0) return;
        
        try {
          this.loading = true;
          for (const studentId of this.selectedStudents) {
            await this.previewReportCard(studentId);
            // Add delay between opening previews
            await new Promise(resolve => setTimeout(resolve, 1000));
          }
        } catch (error) {
          console.error('Error previewing report cards:', error);
        } finally {
          this.loading = false;
        }
      },
  
      async downloadSelectedReportCards() {
        if (this.selectedStudents.length === 0) return;
        
        try {
          this.loading = true;
          for (const studentId of this.selectedStudents) {
            await this.downloadReportCard(studentId);
            // Add delay between downloads
            await new Promise(resolve => setTimeout(resolve, 500));
          }
        } catch (error) {
          console.error('Error downloading report cards:', error);
        } finally {
          this.loading = false;
        }
      },
  
      async fetchStudyYears() {
        try {
          this.loading = true;
          const response = await api.get('/study-years');
          this.studyYears = response.data.data || [];
        } catch (error) {
          console.error('Error fetching study years:', error);
        } finally {
          this.loading = false;
        }
      },
  
      async fetchTerms() {
        if (!this.selectedStudyYear) return;
        
        try {
          this.loading = true;
          this.selectedTerm = '';
          this.terms = [];
          
          const response = await api.get('/terms', {
            params: { study_year_id: this.selectedStudyYear }
          });
          this.terms = response.data.data || [];
        } catch (error) {
          console.error('Error fetching terms:', error);
        } finally {
          this.loading = false;
        }
      },
  
      async fetchClasses() {
        if (!this.selectedTerm) return;
        
        try {
          this.loading = true;
          this.selectedClass = '';
          this.classes = [];
          
          const response = await api.get('/classes');
          this.classes = response.data.data || response.data || [];
        } catch (error) {
          console.error('Error fetching classes:', error);
        } finally {
          this.loading = false;
        }
      },
  
      async fetchSections() {
        if (!this.selectedClass) return;
        
        try {
          this.loading = true;
          this.selectedSection = '';
          this.sections = [];
          
          const response = await api.get(`/classes/${this.selectedClass}/sections`);
          this.sections = response.data.sections || response.data.data || response.data || [];
        } catch (error) {
          console.error('Error fetching sections:', error);
        } finally {
          this.loading = false;
        }
      },
  
      async fetchStudents() {
        if (!this.hasValidFilters) return;
        
        try {
          this.loading = true;
          this.filtersApplied = true;
          this.students = [];
          this.selectedStudents = [];
          this.allSelected = false;
          
          let params = {
            term_id: this.selectedTerm,
            with: 'class,section'
          };
          
          if (this.selectedClass) {
            params.class_id = this.selectedClass;
          }
          
          if (this.selectedSection) {
            params.section_id = this.selectedSection;
          }
          
          const response = await api.get('/students', { params });
          this.students = response.data.students || response.data.data || response.data || [];
        } catch (error) {
          console.error('Error fetching students:', error);
        } finally {
          this.loading = false;
        }
      },
  
      async emailReportCard(studentId) {
      try {
        this.loading = true;
        const response = await api.post(`/report-cards/${studentId}/email`, {
          term_id: this.selectedTerm,
          template: this.selectedTemplate
        });
        this.$toast.success('Report card emailed successfully');
      } catch (error) {
        console.error('Error emailing report card:', error);
        this.$toast.error('Failed to email report card');
      } finally {
        this.loading = false;
      }
    },
  
      selectAllStudents() {
        this.allSelected = !this.allSelected;
        if (this.allSelected) {
          this.selectedStudents = this.students.map(student => student.id);
        } else {
          this.selectedStudents = [];
        }
      },
  
      toggleAllSelection() {
        if (this.allSelected) {
          this.selectedStudents = this.students.map(student => student.id);
        } else {
          this.selectedStudents = [];
        }
      },
  
      async emailSelectedReportCards() {
        if (this.selectedStudents.length === 0) return;
        
        try {
          this.loading = true;
          let successCount = 0;
          
          for (const studentId of this.selectedStudents) {
            try {
              await this.emailReportCard(studentId);
              successCount++;
            } catch (error) {
              console.error(`Error emailing report card for student ${studentId}:`, error);
            }
          }
          
          this.$toast.success(`Successfully emailed ${successCount} report cards`);
        } catch (error) {
          console.error('Error emailing report cards:', error);
        } finally {
          this.loading = false;
        }
      }
    }
  };
  </script>
  
  <style scoped>
  /* Base Styles */
  :root {
    --primary-color: #1a237e !important;
    --primary-light: #534bae;
    --primary-dark: #000051;
    --secondary-color: #f5f5f5;
    --accent-color: #4fc3f7;
    --text-color: #333;
    --text-light: #666;
    --border-color: #e0e0e0;
    --success-color: #4caf50;
    --warning-color: #ff9800;
    --error-color: #f44336;
    --card-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    --transition: all 0.3s ease;
  }
  
  * {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
  }
  
  body {
    font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
    line-height: 1.6;
    color: var(--text-color);
    background-color: #f9f9f9;
  }
  
  /* Layout */
  .report-card-generator {
    max-width: 1200px;
    margin: 0 auto;
    padding: 20px;
  }
  
  .header {
    text-align: center;
    margin-bottom: 30px;
    color: var(--primary-color);
  }
  
  .header h2 {
    font-size: 28px;
    font-weight: 600;
    margin-bottom: 8px;
  }
  
  .header p {
    color: var(--text-light);
    font-size: 16px;
  }
  
  /* Cards */
  .card {
    background: #0a192f !important;
    border-radius: 8px;
    box-shadow: var(--card-shadow);
    padding: 24px;
    margin-bottom: 24px;
    transition: var(--transition);
  }
  
  .card:hover {
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.15);
  }
  
  /* Filters */
  .filters-container h3 {
    margin-bottom: 20px;
    color: var(--primary-color);
    font-size: 18px;
    font-weight: 600;
    border-bottom: 1px solid var(--border-color);
    padding-bottom: 10px;
  }
  
  .filter-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    gap: 20px;
    margin-bottom: 20px;
  }
  
  .filter-group {
    margin-bottom: 5px;
  }
  
  .filter-group label {
    display: block;
    margin-bottom: 8px;
    font-weight: 500;
    color: var(--text-color);
    font-size: 14px;
  }
  
  .modern-select {
    width: 100%;
    padding: 10px 12px;
    border: 1px solid var(--border-color);
    border-radius: 6px;
    font-size: 14px;
    color: #0a192f !important;
    background-color: white;
    transition: var(--transition);
    appearance: none;
    background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e");
    background-repeat: no-repeat;
    background-position: right 10px center;
    background-size: 16px;
  }
  
  .modern-select:focus {
    outline: none;
    border-color: var(--primary-light);
    box-shadow: 0 0 0 2px rgba(26, 35, 126, 0.2);
  }
  
  .search-btn {
    background-color: var(--primary-color);
    color: white;
    padding: 12px 24px;
    border: none;
    border-radius: 6px;
    cursor: pointer;
    font-size: 14px;
    font-weight: 500;
    transition: var(--transition);
    display: inline-flex;
    align-items: center;
    gap: 8px;
  }
  
  .search-btn:hover {
    background-color: var(--primary-light);
    transform: translateY(-1px);
  }
  
  .search-btn:disabled {
    background-color: #cccccc;
    cursor: not-allowed;
    transform: none;
  }
  
  /* Students Section */
  .section-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
    flex-wrap: wrap;
    gap: 15px;
  }
  
  .section-header h3 {
    color: var(--primary-color);
    font-size: 18px;
    font-weight: 600;
  }
  
  .bulk-actions {
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
  }
  
  .table-container {
    overflow-x: auto;
  }
  
  .modern-table {
    width: 100%;
    border-collapse: separate;
    border-spacing: 0;
    font-size: 14px;
  }
  
  .modern-table th {
    background-color: var(--primary-color);
    color: white;
    font-weight: 500;
    text-align: left;
    padding: 12px 16px;
    position: sticky;
    top: 0;
  }
  
  .modern-table th:first-child {
    border-top-left-radius: 6px;
  }
  
  .modern-table th:last-child {
    border-top-right-radius: 6px;
  }
  
  .modern-table td {
    padding: 12px 16px;
    border-bottom: 1px solid var(--border-color);
    vertical-align: middle;
  }
  
  .modern-table tr:last-child td {
    border-bottom: none;
  }
  
  .modern-table tr:hover td {
    background-color: rgba(26, 35, 126, 0.05);
  }
  
  .checkbox-col {
    width: 40px;
    text-align: center;
  }
  
  .actions-col {
    width: 180px;
  }
  
  .student-checkbox {
    width: 16px;
    height: 16px;
    cursor: pointer;
  }
  
  .student-name {
    font-weight: 500;
    color: white !important;
  }
  
  .action-buttons {
    display: flex;
    gap: 6px;
  }
  
  /* Buttons */
  .action-btn {
    padding: 8px 12px;
    border: 1px solid var(--border-color);
    border-radius: 4px;
    background-color: white;
    color: var(--text-color);
    cursor: pointer;
    font-size: 13px;
    font-weight: 500;
    transition: var(--transition);
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }
  
  .action-btn:hover {
    background-color: #f5f5f5;
    transform: translateY(-1px);
  }
  
  .action-btn.primary {
    background-color: var(--primary-color);
    color: white;
    border-color: var(--primary-color);
  }
  
  .action-btn.primary:hover {
    background-color: var(--primary-light);
  }
  
  .action-btn.secondary {
    background-color: var(--secondary-color);
    border-color: var(--secondary-color);
  }
  
  .action-btn.small {
    padding: 6px 8px;
    font-size: 12px;
  }
  
  .action-btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
  
  /* Loading States */
  .loading-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(255, 255, 255, 0.8);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
  }
  
  .loading-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
    background: white;
    padding: 30px 40px;
    border-radius: 8px;
    box-shadow: var(--card-shadow);
  }
  
  .spinner {
    width: 40px;
    height: 40px;
    border: 4px solid rgba(26, 35, 126, 0.1);
    border-radius: 50%;
    border-top: 4px solid var(--primary-color);
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
  
  /* Empty State */
  .empty-state {
    text-align: center;
    padding: 40px 20px;
  }
  
  .empty-state i {
    font-size: 48px;
    color: var(--primary-light);
    margin-bottom: 16px;
  }
  
  .empty-state h4 {
    font-size: 18px;
    margin-bottom: 8px;
    color: var(--text-color);
  }
  
  .empty-state p {
    color: var(--text-light);
    max-width: 400px;
    margin: 0 auto;
  }
  
  /* PDF Modal */
  .pdf-modal {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 2000;
  }
  
  .modal-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.7);
  }
  
  .modal-container {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 90%;
    max-width: 900px;
    max-height: 90vh;
    display: flex;
    flex-direction: column;
    background: white;
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  }
  
  .modal-header {
    padding: 16px 24px;
    background-color: var(--primary-color);
    color: white;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  
  .modal-header h3 {
    font-size: 18px;
    font-weight: 500;
  }
  
  .close-btn {
    background: none;
    border: none;
    color: white;
    font-size: 20px;
    cursor: pointer;
    padding: 4px;
    transition: var(--transition);
  }
  
  .close-btn:hover {
    opacity: 0.8;
  }
  
  .pdf-viewer {
    height: 70vh;
    background: #f0f0f0;
  }
  
  .loading-pdf {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
    gap: 16px;
  }
  
  .modal-footer {
    padding: 16px 24px;
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    background-color: #f9f9f9;
    border-top: 1px solid var(--border-color);
  }
  
  /* Responsive Adjustments */
  @media (max-width: 768px) {
    .filter-grid {
      grid-template-columns: 1fr;
    }
    
    .section-header {
      flex-direction: column;
      align-items: flex-start;
    }
    
    .bulk-actions {
      width: 100%;
    }
    
    .action-btn {
      flex: 1;
      justify-content: center;
    }
    
    .modal-container {
      width: 95%;
      height: 95vh;
    }
  }
  
  @media (max-width: 480px) {
    .modal-footer {
      flex-direction: column;
    }
    
    .action-btn {
      width: 100%;
    }
  }
  </style>